import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBaseUrl, getWsBaseUrl } from '../utils/api';

const WORKLET_URL = `${process.env.PUBLIC_URL || ''}/audio/pcm-capture-processor.js`;
const STOP_GRACE_MS = 400;

function appendSegment(base, segment) {
  if (!segment) return base;
  if (!base) return segment;
  const needsSpace = base.length > 0 && !/\s$/.test(base) && !/^\s/.test(segment);
  return base + (needsSpace ? ' ' : '') + segment;
}

export function useSpeechInput() {
  const [isListening, setIsListening] = useState(false);
  const [hasTranscript, setHasTranscript] = useState(false);
  const [speechInputEnabled, setSpeechInputEnabled] = useState(null);
  const [speechError, setSpeechError] = useState(null);

  const baseTextRef = useRef('');
  const committedRef = useRef('');
  const interimRef = useRef('');
  const onUpdateRef = useRef(null);

  const wsRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const stoppingRef = useRef(false);

  const isSpeechSupported =
    typeof window !== 'undefined' &&
    !!window.MediaStream &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window.AudioWorkletNode !== 'undefined';

  const showSpeechInput = isSpeechSupported && speechInputEnabled === true;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/speech/config`);
        if (!response.ok) {
          throw new Error(`Speech config request failed (${response.status})`);
        }
        const data = await response.json();
        if (!cancelled) {
          setSpeechInputEnabled(Boolean(data.enabled));
        }
      } catch {
        if (!cancelled) {
          setSpeechInputEnabled(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const refreshDisplay = useCallback(() => {
    if (typeof onUpdateRef.current === 'function') {
      const display = appendSegment(
        appendSegment(baseTextRef.current, committedRef.current),
        interimRef.current
      );
      onUpdateRef.current(display);
    }
  }, []);

  const markTranscriptReceived = useCallback((text) => {
    if (text && text.trim()) {
      setHasTranscript(true);
    }
  }, []);

  const teardownAudio = useCallback(async () => {
    if (workletNodeRef.current) {
      try {
        workletNodeRef.current.port.onmessage = null;
        workletNodeRef.current.disconnect();
      } catch {
        // ignore
      }
      workletNodeRef.current = null;
    }
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
      } catch {
        // ignore
      }
      sourceNodeRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        await audioContextRef.current.close();
      } catch {
        // ignore
      }
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const sendStopSignal = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }
    try {
      ws.send(JSON.stringify({ type: 'stop' }));
    } catch {
      // ignore
    }
  }, []);

  const closeWebSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (!ws) {
      return;
    }
    try {
      ws.close();
    } catch {
      // ignore
    }
  }, []);

  const teardownWebSocket = useCallback(() => {
    sendStopSignal();
    closeWebSocket();
  }, [closeWebSocket, sendStopSignal]);

  const finalizeTranscript = useCallback(() => {
    if (interimRef.current) {
      committedRef.current = appendSegment(committedRef.current, interimRef.current);
      interimRef.current = '';
    }
    refreshDisplay();
  }, [refreshDisplay]);

  const stopListening = useCallback(async () => {
    if (stoppingRef.current) {
      return;
    }
    stoppingRef.current = true;
    setIsListening(false);
    setHasTranscript(false);

    await teardownAudio();

    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      sendStopSignal();
      await new Promise((resolve) => setTimeout(resolve, STOP_GRACE_MS));
    }

    finalizeTranscript();
    closeWebSocket();
    onUpdateRef.current = null;
    stoppingRef.current = false;
  }, [closeWebSocket, finalizeTranscript, sendStopSignal, teardownAudio]);

  const startListening = useCallback(
    async (baseText, onTranscriptUpdate) => {
      if (!isSpeechSupported) {
        setSpeechError('Speech input is not supported in this browser.');
        return;
      }
      if (isListening) {
        await stopListening();
      }

      setSpeechError(null);
      stoppingRef.current = false;
      setHasTranscript(false);
      baseTextRef.current = baseText || '';
      committedRef.current = '';
      interimRef.current = '';
      onUpdateRef.current = onTranscriptUpdate;
      refreshDisplay();

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        setSpeechError('Microphone access denied.');
        return;
      }
      mediaStreamRef.current = stream;

      const token = localStorage.getItem('access_token');
      if (!token) {
        setSpeechError('Authentication required.');
        await teardownAudio();
        return;
      }

      const wsUrl = `${getWsBaseUrl()}/ws/transcribe`;
      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      const handleTranscriptMessage = (data) => {
        if (data.type === 'partial') {
          interimRef.current = data.text || '';
          markTranscriptReceived(interimRef.current);
          refreshDisplay();
        } else if (data.type === 'final') {
          committedRef.current = appendSegment(committedRef.current, data.text || '');
          interimRef.current = '';
          markTranscriptReceived(data.text);
          refreshDisplay();
        } else if (data.type === 'error') {
          setSpeechError(data.message || 'Transcription failed.');
          stopListening();
        }
      };

      try {
        await new Promise((resolve, reject) => {
          const onOpen = () => {
            ws.send(JSON.stringify({ type: 'auth', token }));
          };
          const onMessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              if (data.type === 'ready') {
                cleanup();
                resolve();
                return;
              }
              if (data.type === 'error') {
                cleanup();
                reject(new Error(data.message || 'Authentication failed'));
                return;
              }
              handleTranscriptMessage(data);
            } catch {
              // ignore non-JSON
            }
          };
          const onError = () => {
            cleanup();
            reject(new Error('WebSocket connection failed'));
          };
          const onClose = (event) => {
            cleanup();
            if (event.code === 4401) {
              reject(new Error('Authentication failed'));
              return;
            }
            reject(new Error('WebSocket closed before speech service was ready'));
          };
          const cleanup = () => {
            ws.removeEventListener('open', onOpen);
            ws.removeEventListener('message', onMessage);
            ws.removeEventListener('error', onError);
            ws.removeEventListener('close', onClose);
          };

          ws.addEventListener('open', onOpen);
          ws.addEventListener('message', onMessage);
          ws.addEventListener('error', onError);
          ws.addEventListener('close', onClose);
        });
      } catch (err) {
        const message = err?.message || '';
        const isAuthError = /auth|token|expired/i.test(message);
        setSpeechError(
          isAuthError ? 'Authentication failed. Please sign in again.' : (message || 'Could not connect to speech service.')
        );
        teardownWebSocket();
        await teardownAudio();
        return;
      }

      ws.onmessage = (event) => {
        try {
          handleTranscriptMessage(JSON.parse(event.data));
        } catch {
          // ignore
        }
      };
      ws.onclose = () => {
        if (wsRef.current !== ws || stoppingRef.current) {
          return;
        }
        void stopListening();
      };

      try {
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        await audioContext.audioWorklet.addModule(WORKLET_URL);

        const source = audioContext.createMediaStreamSource(stream);
        sourceNodeRef.current = source;

        const worklet = new AudioWorkletNode(audioContext, 'pcm-capture-processor');
        workletNodeRef.current = worklet;

        worklet.port.onmessage = (event) => {
          const activeWs = wsRef.current;
          if (!activeWs || activeWs.readyState !== WebSocket.OPEN) return;
          try {
            activeWs.send(event.data);
          } catch {
            // ignore send failures during teardown
          }
        };

        const silentGain = audioContext.createGain();
        silentGain.gain.value = 0;

        source.connect(worklet);
        worklet.connect(silentGain);
        silentGain.connect(audioContext.destination);
        await audioContext.resume();
        setIsListening(true);
      } catch {
        setSpeechError('Could not start audio capture.');
        teardownWebSocket();
        await teardownAudio();
      }
    },
    [
      isListening,
      isSpeechSupported,
      markTranscriptReceived,
      refreshDisplay,
      stopListening,
      teardownAudio,
      teardownWebSocket,
    ]
  );

  useEffect(() => {
    return () => {
      teardownWebSocket();
      teardownAudio();
    };
  }, [teardownAudio, teardownWebSocket]);

  return {
    isListening,
    hasTranscript,
    canStopListening: isListening && hasTranscript,
    showSpeechInput,
    speechError,
    isSpeechSupported,
    startListening,
    stopListening,
    clearSpeechError: () => setSpeechError(null),
  };
}
